import React, { useState, useEffect } from "react";
import { FBProfile } from "~/.server/api";
import { cn } from "~/lib/utils";
// Removed import of dailyRewardCooldowns

// Define the structure of the dailyReward metadata we expect
type DailyRewardData = FBProfile["metadata"]["dailyReward"];

interface DailyRewardTriggerProps {
  dailyRewardData: DailyRewardData;
  onClick: () => void; // Function to open the modal
  className?: string;
}

const DailyRewardTrigger: React.FC<DailyRewardTriggerProps> = ({
  dailyRewardData,
  onClick,
  className,
}) => {
  // State variables
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const [maxDuration, setMaxDuration] = useState<number>(1);
  const [isClaimReady, setIsClaimReady] = useState(false);
  
  // Check if daily rewards have expired
  const isExpired = dailyRewardData?.endDate ? new Date(dailyRewardData.endDate) < new Date() : false;
  
  // If expired, don't render anything
  // if (isExpired) {
  //   return null;
  // }

  useEffect(() => {
    let countdownIntervalId: NodeJS.Timeout | null = null;
    let targetTimestamp: number | null = null;
    let currentMaxDuration = 1;

    const claimsMade = dailyRewardData?.claimsMadeThisCycle ?? 0;
    const lastClaimTime = dailyRewardData?.lastClaimTimestamp
      ? new Date(dailyRewardData.lastClaimTimestamp).getTime()
      : null;
    const cycleCooldownEndTime = dailyRewardData?.cycleCooldownUntilTimestamp
      ? new Date(dailyRewardData.cycleCooldownUntilTimestamp).getTime()
      : null;
    // Get intervals from metadata, provide fallback
    const intervals = dailyRewardData?.intervalsSeconds ?? [30, 30, 30]; // Use correct name

    // Determine current state and target time
    if (claimsMade >= 3) {
      // In 24-hour cooldown
      if (cycleCooldownEndTime && cycleCooldownEndTime > Date.now()) {
        targetTimestamp = cycleCooldownEndTime;
        // Calculate max duration based on the *start* of the cooldown
        if (lastClaimTime) {
          currentMaxDuration = (cycleCooldownEndTime - lastClaimTime) / 1000;
        } else {
          // Use cycleResetSeconds from metadata if available, else default
          currentMaxDuration = dailyRewardData?.cycleResetSeconds ?? 86400;
        }
      } else {
        // Cooldown finished
        setIsClaimReady(true);
      }
    } else {
      // Cycle active
      if (lastClaimTime) {
        // Use interval from metadata
        const cooldownSeconds = intervals[claimsMade] ?? 30; // Fallback if array is malformed
        targetTimestamp = lastClaimTime + cooldownSeconds * 1000;
        currentMaxDuration = cooldownSeconds;
      } else {
        // Initial state, ready for first claim
        setIsClaimReady(true);
      }
    }

    setMaxDuration(currentMaxDuration > 0 ? currentMaxDuration : 1);

    // Function to check if the target time has passed and update state
    const checkTime = () => {
      if (targetTimestamp) {
        const now = Date.now();
        const secondsLeft = Math.max(0, (targetTimestamp - now) / 1000);
        setRemainingTime(secondsLeft);

        if (secondsLeft <= 0) {
          setIsClaimReady(true);
          if (countdownIntervalId) clearInterval(countdownIntervalId);
        } else {
          if (isClaimReady) {
            setIsClaimReady(false);
          }
        }
      } else {
        setIsClaimReady(true);
        setRemainingTime(0);
      }
    };

    checkTime(); // Initial check

    if (targetTimestamp && Date.now() < targetTimestamp) {
      countdownIntervalId = setInterval(checkTime, 1000);
    }

    // Cleanup intervals
    return () => {
      if (countdownIntervalId) clearInterval(countdownIntervalId);
    };
  }, [dailyRewardData]);

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex flex-col items-center cursor-pointer transition-all duration-200 ease-in-out",
        isClaimReady && !isExpired ? "animate-bounce" : "",
        isExpired ? "opacity-50" : "",
        className
      )}
      style={isClaimReady && !isExpired ? {
        animation: "pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite"
      } : {}}
    >
      <button
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-200 ease-in-out bg-transparent hover:scale-105"
      >
      {/* Center content */}
      <div className="relative z-10 flex items-center justify-center w-full h-full">
        {isClaimReady ? (
          /* Custom Gift SVG when claimable */
          <img
            src="/gift-svgrepo-com.svg"
            alt="Gift"
            // Sized by ink: this SVG's artwork is only 73.7% of its 1024
            // viewBox, so it needs a 26px box to draw ~19px of gift. The -1px
            // nudge corrects its artwork sitting 5% low inside the viewBox.
            className="w-[26px] h-[26px] -translate-y-px"
          />
        ) : (
          /* Custom Gift SVG during cooldown - full opacity now */
          <img
            src="/gift-svgrepo-com.svg"
            alt="Gift"
            // Sized by ink: this SVG's artwork is only 73.7% of its 1024
            // viewBox, so it needs a 26px box to draw ~19px of gift. The -1px
            // nudge corrects its artwork sitting 5% low inside the viewBox.
            className="w-[26px] h-[26px] -translate-y-px"
          />
        )}
      </div>
      </button>
      
      {/* Rewards text below button - slightly raised with negative margin */}
      <span className="hidden md:block text-text1 text-[0.5rem] -mt-0.5">
        Rewards
      </span>
    </div>
  );
};

export default DailyRewardTrigger;
